import { MapViewerNode } from '@3ddv/dvm-internal';
import { MapViewerComponent } from '@3ddv/ngx-dvm-internal';
import { ApplicationRef, Injectable, NgZone, inject } from '@angular/core';
import { PriceScaleModel, SectionModel } from 'projects/common/igsu-models/definitions/section.model';
import { BehaviorSubject } from 'rxjs';
import { SeatSelectionState } from '../../seat-selection/data-access/seat-selection.state';
import { RunInZone } from '../utils/run-in-zone';

/**
 * SectionsService is an injectable service that manages the state and data of venue
 * sections within the seat selection process. It provides functionalities to select,
 * unselect, filter, and manage the available and selected sections using RxJS's
 * BehaviorSubjects. It interacts with the MapViewerComponent to visualize and
 * interact with the sections. The service also integrates with SeatSelectionState
 * to manage the overall state of the seat selection route, allowing for a single source of truth to
 * manage UI state.
 */
@Injectable({
  providedIn: 'root',
})
export class SectionsService {
  protected ngZone = inject(NgZone);
  protected cd = inject(ApplicationRef);
  private _seatSelectionState = inject(SeatSelectionState);

  private readonly _availableSectionsData = new BehaviorSubject<SectionModel[] | null>(null);
  public readonly availableSectionData$ = this._availableSectionsData.asObservable();

  private readonly _selectedSectionData = new BehaviorSubject<SectionModel | undefined>(undefined);
  public readonly selectedSectionData$ = this._selectedSectionData.asObservable();

  public generalAdmissionSections = new Set();

  /**
   * Retrieves the available section data.
   * @returns The current value of available section data.
   */
  public getAvailableSectionData = () => {
    return this._availableSectionsData.getValue();
  };

  /**
   * Sets the available section data.
   * @param availableSectionData Array of SectionModel to set as available.
   */
  @RunInZone()
  public setAvailableSectionData(availableSectionData: SectionModel[]) {
    this._availableSectionsData.next(availableSectionData);
  }

  public resetAvailableSectionData = () => {
    this._availableSectionsData.next(null);
  };

  /**
   * Retrieves the selected section data.
   * @returns The current value of selected section data.
   */
  public getSelectedSectionData = () => {
    return this._selectedSectionData.getValue();
  };

  /**
   * Selects a section based on the provided MapViewerNode. It updates the selected section data and seat selection state accordingly.
   * @param section The section MapViewerNode to select.
   */
  @RunInZone()
  public selectSection = (section: MapViewerNode) => {
    this.setSelectedSectionData(section);

    this.ngZone.run(() => {
      this._seatSelectionState.setSelectState();
    });
  };

  /**
   * Sets the selected section data based on the provided MapViewerNode.
   * @param section The section MapViewerNode to set as selected.
   */
  public setSelectedSectionData = (section: MapViewerNode) => {
    const provenueSection = this.getSectionFromNodeID(section.id);
    this._selectedSectionData.next(provenueSection);
  };

  /**
   * Filters price scales to include only those that have matching buyer types.
   * @param priceScales - The array of price scales to evaluate.
   * @param selectedTicketsBuyerTypes - Buyer types from selected tickets to match against.
   * @return PriceScaleModel[] - An array of price scales that have matching buyer types.
   */
  public filterPriceScalesWithMatchingBuyerTypes(
    priceScales: PriceScaleModel[],
    selectedTicketsBuyerTypes: number[]
  ): PriceScaleModel[] {
    return priceScales.filter(priceScale =>
      priceScale.buyerTypes.some(buyerType =>
        selectedTicketsBuyerTypes.some(selectedType => +selectedType === +buyerType.buyerType.buyerTypeID)
      )
    );
  }

  /**
   * Toggles the filter state in the seat selection and unselects any selected section.
   * @param sectionViewerService The service of the MapViewerComponent for unselecting nodes.
   */
  public toggleFilter = (sectionViewerService: MapViewerComponent['service'] | null) => {
    sectionViewerService?.unselectAll();
    this._selectedSectionData.next(null!);

    if (this._seatSelectionState.getState() === 'filter') {
      this._seatSelectionState.setState('none');
    } else {
      this._seatSelectionState.setState('filter');
    }
  };

  /**
   * Unselects the currently selected section and resets the seat selection state.
   */
  public unselectSectionSync = () => {
    this._selectedSectionData.next(null!);
    this._seatSelectionState.resetState();
  };

  /**
   * Unselects the currently selected section and resets the seat selection state.
   */
  public unselectSection = () => {
    this._seatSelectionState.resetState();
    // This `setTimeout()` is done so whenever a container is closed with an animation,
    // the animation is first finished with the content present within,
    // otherwise the content would just dissapear before the container is finishing its animation
    setTimeout(() => this._selectedSectionData.next(null!), 275);
  };

  /**
   * Unselects the section on the map viewer and updates the local section state.
   * @param sectionViewerService The service of the MapViewerComponent for unselecting nodes.
   */
  public unselectSectionAndUpdateMap = (sectionViewerService: MapViewerComponent['service'] | null) => {
    const isUnselectSuccess = sectionViewerService?.unselectAll();

    if (isUnselectSuccess) {
      this.unselectSection();
    }
  };

  /**
   * Retrieves the section data from the available sections by node ID.
   * @param nodeID The identifier of the MapViewerNode to find in available sections.
   * @returns The SectionModel that corresponds to the given node ID.
   */
  public getSectionFromNodeID = (nodeID: MapViewerNode['id']) => {
    return this._availableSectionsData.getValue()?.find(section => +section.sectionID === +nodeID);
  };

  /**
   * Checks if any section is currently selected.
   * @returns A boolean indicating if a section is selected.
   */
  public isSectionSelected = (): boolean => {
    return !!this._selectedSectionData.getValue();
  };

  public setGeneralAdmissionSections = (generalAdmissionSections: string[]) => {
    this.generalAdmissionSections = new Set(generalAdmissionSections);
  };

  public isGeneralAdmissionSection = (sectionID?: string) => {
    if (!sectionID) {
      return false;
    }
    return this.generalAdmissionSections.has(sectionID);
  };

  public isCurrentSectionGeneralAdmission = () => {
    return this.isGeneralAdmissionSection(this.getSelectedSectionData()?.sectionID.toString());
  };
}
